import React from "react"
import {useTranslation} from "react-i18next"
import {BuildingOffice2Icon, ChevronDownIcon, CircleStackIcon} from "@heroicons/react/24/outline"
import {parseISO} from "date-fns"
import {twMerge} from "tailwind-merge"

import Button from "../../../components/Button.tsx"
import {FieldLabel} from "../../../components/fields/FieldLabel.tsx"
import {DateBase} from "../../../components/formElements/DateField.tsx"
import {i18n} from "../../../i18n.ts"
import {
  ASalesAssistantDealStatuses,
  ASalesAssistantUpdateCenterEntryTypeValues,
} from "../../../services/types.generated.ts"
import {formatSeconds, getFullName} from "../../../utils/index.ts"
import {StepCard} from "../../SalesAssistant/components/UpdateCenterStep.tsx"
import {formatDay} from "../period.ts"
import {
  entryInitials,
  feedDealName,
  formatSessionClock,
  formatSessionTime,
  groupEntriesByDay,
  summarizeEntry,
  TKlaraFeedEntry,
} from "../sessions.ts"
import {TDashboardClock, useDashboardClock} from "../useDashboardClock.ts"
import {formatDate} from "./formatDate.ts"

const EMPTY_DATES: string[] = []

/** How many pages the list loads while looking for a selected session before it gives up. */
export const SELECTED_ENTRY_MAX_PAGES = 5

/** Lets a day label move the feed's upper bound to an earlier day, within `[min, max]` (YYYY-MM-DD, inclusive). */
export type TSessionListJump = {
  min?: string
  max: string
  onJump: (date: string) => void
}

type TProps = {
  entries: readonly TKlaraFeedEntry[]
  /** Caps the pages loaded while looking for the selected session. */
  loadedPages?: number
  hasNextPage: boolean
  /** Any feed fetch, including a refresh of loaded pages: paging waits for it so it never cancels the refresh. */
  isFetching: boolean
  fetchNextPage: () => void
  selectedEntryId?: number
  /** While true, further pages are loaded until the selected session appears, which is then scrolled into view. */
  scrollToSelected?: boolean
  onScrolledToSelected?: () => void
  /** Without it the day labels are plain headings. */
  jump?: TSessionListJump
  /** "timeline" lists one deal's sessions as compact dot-and-text entries, without day separators. */
  variant?: "feed" | "timeline"
}

export const SessionList: React.FC<TProps> = ({
  entries,
  loadedPages = 0,
  hasNextPage,
  isFetching,
  fetchNextPage,
  selectedEntryId,
  scrollToSelected = false,
  onScrolledToSelected,
  jump,
  variant = "feed",
}) => {
  const {t} = useTranslation()
  const [jumpDay, setJumpDay] = React.useState<string>()
  const selectedNode = React.useRef<HTMLElement>(null)

  const isSelectedLoaded = selectedEntryId !== undefined && entries.some(entry => entry.id === selectedEntryId)
  const shouldLoadMoreForSelection =
    scrollToSelected &&
    selectedEntryId !== undefined &&
    !isSelectedLoaded &&
    hasNextPage &&
    !isFetching &&
    loadedPages < SELECTED_ENTRY_MAX_PAGES

  React.useEffect(() => {
    if (shouldLoadMoreForSelection) {
      fetchNextPage()
    }
  }, [shouldLoadMoreForSelection, fetchNextPage])

  React.useEffect(() => {
    if (scrollToSelected && isSelectedLoaded) {
      selectedNode.current?.scrollIntoView({block: "start"})
      onScrolledToSelected?.()
    }
  }, [scrollToSelected, isSelectedLoaded, onScrolledToSelected])

  const days = React.useMemo(() => groupEntriesByDay(entries), [entries])
  const clock = useDashboardClock()
  const {timeZone} = clock

  const showMore = hasNextPage && (
    <div className={"flex justify-center pt-2 pb-1"}>
      <button
        type={"button"}
        disabled={isFetching}
        onClick={() => fetchNextPage()}
        className={
          "rounded-lg border border-cr-grey-15 bg-cr-white px-3.5 py-[7px] text-xs font-semibold text-cr-blue transition-colors hover:border-cr-blue-mid disabled:cursor-wait disabled:opacity-50"
        }
      >
        {t("KlaraDashboard_UpdateCenter_ShowMore")}
      </button>
    </div>
  )

  if (variant === "timeline") {
    return (
      <div className={"flex flex-col"}>
        {entries.map(entry => (
          <TimelineSession key={entry.id} entry={entry} clock={clock} />
        ))}
        {showMore}
      </div>
    )
  }

  return (
    <div className={"flex flex-col gap-3"}>
      {days.map(day => (
        <section key={day.date} className={"flex flex-col gap-3"} aria-label={formatDayLabel(day.date)}>
          {jump ? (
            <JumpableDaySeparator
              date={day.date}
              earlierDates={
                jumpDay === day.date ? days.map(other => other.date).filter(other => other < day.date) : EMPTY_DATES
              }
              jump={jump}
              isOpen={jumpDay === day.date}
              onToggle={() => setJumpDay(current => (current === day.date ? undefined : day.date))}
              onJump={date => {
                setJumpDay(undefined)
                jump.onJump(date)
              }}
            />
          ) : (
            <DaySeparator date={day.date} />
          )}
          {day.entries.map(entry => (
            <Session
              key={entry.id}
              entry={entry}
              isSelected={entry.id === selectedEntryId}
              timeZone={timeZone}
              ref={entry.id === selectedEntryId ? selectedNode : undefined}
            />
          ))}
        </section>
      ))}
      {showMore}
    </div>
  )
}

const formatDayLabel = (date: string) => formatDate(parseISO(date).getTime(), "EEE d MMM yyyy")

const DAY_PILL_CLASS_NAME =
  "inline-flex min-w-[150px] items-center justify-center gap-1.5 rounded-full border border-cr-blue-mid-light bg-cr-white px-3.5 py-1 text-xs font-semibold text-cr-slate-90"

const DaySeparator: React.FC<{date: string}> = ({date}) => (
  <div className={"mt-1.5 mb-1 flex items-center"}>
    <div className={"h-px grow bg-cr-blue-mid-light"} />
    <h3 className={DAY_PILL_CLASS_NAME}>{formatDayLabel(date)}</h3>
    <div className={"h-px grow bg-cr-blue-mid-light"} />
  </div>
)

const JumpableDaySeparator: React.FC<{
  date: string
  /** Loaded days before this one, latest first. */
  earlierDates: string[]
  jump: TSessionListJump
  isOpen: boolean
  onToggle: () => void
  onJump: (date: string) => void
}> = ({date, earlierDates, jump, isOpen, onToggle, onJump}) => {
  const {t} = useTranslation()
  const panelId = React.useId()
  const inputName = `klara-jump-${date}`

  return (
    <div className={"flex flex-col gap-3"}>
      <div className={"mt-1.5 mb-1 flex items-center"}>
        <div className={"h-px grow bg-cr-blue-mid-light"} />
        <h3>
          <button
            type={"button"}
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={onToggle}
            className={twMerge(
              DAY_PILL_CLASS_NAME,
              "transition-colors hover:border-cr-blue-mid hover:bg-cr-blue-super-light"
            )}
          >
            {formatDayLabel(date)}
            <ChevronDownIcon className={"size-3 text-cr-slate-60"} aria-hidden={"true"} />
          </button>
        </h3>
        <div className={"h-px grow bg-cr-blue-mid-light"} />
      </div>
      {isOpen && (
        <div
          id={panelId}
          role={"group"}
          aria-label={t("KlaraDashboard_UpdateCenter_JumpTo")}
          className={"flex flex-col gap-3 rounded-xl border border-cr-blue-light bg-cr-white p-3 shadow-lg"}
        >
          <span className={"text-xs text-cr-slate-60"}>{t("KlaraDashboard_UpdateCenter_JumpTo")}</span>
          {earlierDates.length > 0 && (
            <div className={"flex flex-wrap gap-2"}>
              {earlierDates.map(earlier => (
                <Button key={earlier} variant={"outlined"} color={"gray"} size={"xs"} onClick={() => onJump(earlier)}>
                  {formatDayLabel(earlier)}
                </Button>
              ))}
            </div>
          )}
          <FieldLabel name={inputName} label={t("KlaraDashboard_UpdateCenter_JumpToDate")} variant={"small"} hideError>
            <DateBase
              name={inputName}
              value={parseISO(date)}
              onChange={selected => onJump(formatDay(selected))}
              minDate={jump.min === undefined ? undefined : parseISO(jump.min)}
              maxDate={parseISO(jump.max)}
              calendarClassName={"w-full"}
            />
          </FieldLabel>
        </div>
      )}
    </div>
  )
}

const Session: React.FC<{
  entry: TKlaraFeedEntry
  isSelected: boolean
  timeZone: string | undefined
  ref?: React.Ref<HTMLElement>
}> = ({entry, isSelected, timeZone, ref}) => {
  const {t} = useTranslation()
  const summary = summarizeEntry(entry)
  const title = sessionTitle(entry)

  return (
    <article
      ref={ref}
      aria-label={title}
      aria-current={isSelected ? "true" : undefined}
      className={twMerge(
        "flex scroll-mt-4 flex-col gap-4 rounded-[14px] border border-cr-blue-light bg-cr-white px-[18px] py-4 transition-colors",
        isSelected && "border-cr-blue bg-cr-blue-super-light ring-[3px] ring-cr-blue-light"
      )}
    >
      <div className={"flex items-start gap-2.5"}>
        {summary.isCrm ? (
          <div className={"flex size-[34px] shrink-0 items-center justify-center rounded-full bg-cr-slate-6"}>
            <CircleStackIcon className={"size-4 text-cr-slate-60"} aria-hidden={"true"} />
          </div>
        ) : (
          <div
            aria-hidden={"true"}
            className={
              "flex size-[34px] shrink-0 items-center justify-center rounded-full bg-cr-blue-light text-[11px] font-bold text-cr-blue-dark"
            }
          >
            {entryInitials(entry)}
          </div>
        )}
        <div className={"flex min-w-0 flex-col gap-px"}>
          <h4 className={"text-[13px] leading-snug font-semibold text-cr-slate-90"}>{title}</h4>
          <SessionMeta entry={entry} timeZone={timeZone} />
        </div>
      </div>
      {entry.update_center_events.map(event => (
        <div key={event.id} className={"flex flex-col"}>
          <div className={"ml-0.5 flex items-center gap-2.5"}>
            <div className={"flex size-[26px] shrink-0 items-center justify-center rounded-full bg-cr-blue-mid"}>
              <BuildingOffice2Icon className={"size-3.5 text-cr-white"} aria-hidden={"true"} />
            </div>
            <span className={"min-w-0 truncate text-[13px] font-bold text-cr-slate-90"}>
              {feedDealName(event.deal)}
            </span>
            {event.deal.status === ASalesAssistantDealStatuses.Won && (
              <span
                data-testid={"badge"}
                className={"shrink-0 rounded-full bg-cr-blue px-[9px] py-0.5 text-[10.5px] font-bold text-cr-white"}
              >
                {t("KlaraDashboard_UpdateCenter_Won")}
              </span>
            )}
          </div>
          <div className={"relative mt-2"}>
            <span aria-hidden={"true"} className={"absolute top-3 bottom-3 left-3.5 w-px bg-cr-blue-light"} />
            {event.update_center_steps.map(step => (
              <StepCard key={step.id} step={step} timeZone={timeZone} compact />
            ))}
          </div>
        </div>
      ))}
    </article>
  )
}

/** "Jana Nováková collected 3 activities for 2 deals", or the CRM system's title for a CRM session. */
const sessionTitle = (entry: TKlaraFeedEntry) => {
  const summary = summarizeEntry(entry)
  const deals = i18n.t("KlaraDashboard_UpdateCenter_Deals", {count: summary.dealsCount})
  const activities = i18n.t("KlaraDashboard_UpdateCenter_Activities", {count: summary.activitiesCount})
  const name = getFullName(entry.sales_person, i18n.t("KlaraDashboard_UpdateCenter_UnknownSalesPerson"))

  return summary.isCrm
    ? i18n.t("KlaraDashboard_UpdateCenter_CrmTitle", {deals, activities})
    : i18n.t("KlaraDashboard_UpdateCenter_SessionTitle", {name, deals, activities})
}

const TIMELINE_DOT_CLASS_NAME: Record<ASalesAssistantUpdateCenterEntryTypeValues, string> = {
  [ASalesAssistantUpdateCenterEntryTypeValues.Voice]: "bg-cr-blue",
  [ASalesAssistantUpdateCenterEntryTypeValues.Assistant]: "bg-cr-slate-30",
  [ASalesAssistantUpdateCenterEntryTypeValues.CrmSystem]: "bg-cr-cyan",
}

/** One session of a deal's history: a source dot, the session title, its steps and "source · time". */
const TimelineSession: React.FC<{entry: TKlaraFeedEntry; clock: TDashboardClock}> = ({entry, clock}) => {
  const title = sessionTitle(entry)

  return (
    <article aria-label={title} className={"flex gap-[11px] pb-3.5"}>
      <span
        aria-hidden={"true"}
        className={twMerge("mt-[5px] size-[9px] shrink-0 rounded-full", TIMELINE_DOT_CLASS_NAME[entry.entry_type])}
      />
      <div className={"min-w-0 flex-1"}>
        <h4 className={"text-[12.5px] font-semibold text-cr-slate-90"}>{title}</h4>
        <div className={"-ml-1"}>
          {entry.update_center_events.flatMap(event =>
            event.update_center_steps.map(step => (
              <StepCard key={step.id} step={step} timeZone={clock.timeZone} compact />
            ))
          )}
        </div>
        <p className={"mt-0.5 text-[10.5px] text-cr-slate-40"}>
          {channelLabel(entry)} · {formatSessionTime(entry.tracked_at, {now: clock.asOf, timeZone: clock.timeZone})}
        </p>
      </div>
    </article>
  )
}

const channelLabel = (entry: TKlaraFeedEntry) =>
  ({
    [ASalesAssistantUpdateCenterEntryTypeValues.Voice]: i18n.t("KlaraDashboard_UpdateCenter_Channel_Voice"),
    [ASalesAssistantUpdateCenterEntryTypeValues.Assistant]: i18n.t("KlaraDashboard_UpdateCenter_Channel_Assistant"),
    [ASalesAssistantUpdateCenterEntryTypeValues.CrmSystem]: i18n.t("KlaraDashboard_UpdateCenter_Channel_CrmSystem"),
  })[entry.entry_type]

const SessionMeta: React.FC<{entry: TKlaraFeedEntry; timeZone: string | undefined}> = ({entry, timeZone}) => {
  const {t} = useTranslation()
  const channel = channelLabel(entry)
  const callDuration = entry.voice_progress_tracker?.call_duration

  const parts = [
    channel,
    callDuration !== undefined &&
      t("KlaraDashboard_UpdateCenter_CallDuration", {duration: formatSeconds(callDuration)}),
    formatSessionClock(entry.tracked_at, timeZone),
  ].filter(Boolean)

  return <div className={"text-[11px] text-cr-slate-60"}>{parts.join(" · ")}</div>
}
